import { getAddresses, googleMapsLoader } from '@iheartjane/geolocation';
import {
  Flex,
  LocationPinFilledIcon,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import debounce from 'lodash-es/debounce';
import { useEffect, useRef, useState } from 'react';

import type { AddressAutocompleteProps } from './addressAutocomplete.types';
import { GoogleFooter } from './googleFooter';

const sameOptions = (current: string[], next: string[]) =>
  current.length === next.length &&
  current.every((option, index) => option === next[index]);

export const AddressAutocomplete = ({
  'aria-label': ariaLabel,
  autoFocus,
  clearIcon,
  countries = ['us', 'ca', 'pr'],
  'data-testid': testId,
  defaultValue = '',
  disableMobileInputStyling = false,
  errorMessage,
  name,
  googlePlacesKey,
  isForm,
  label,
  labelHidden,
  onBlur,
  onChange,
  onSelection,
  locationIcon,
  placeholder,
}: AddressAutocompleteProps) => {
  const [options, setOptions] = useState<string[]>([]);

  const TextFieldComponent = isForm ? Form.TextField : TextField;

  const latestInput = useRef('');

  const fetchAddresses = debounce((value: string) => {
    getAddresses({
      countries,
      input: value,
      loader: () => googleMapsLoader(googlePlacesKey || ''),
    }).then((result) => {
      // Drop responses for an older input that resolve after a newer one.
      if (value !== latestInput.current) return;

      const next = result.map(
        (location: { label: string; value: string }) => location.value
      );

      // Reuse the array if unchanged, so a late response doesn't re-render.
      setOptions((current) => (sameOptions(current, next) ? current : next));
    });
  }, 200);

  const handleGetAddresses = (value: string) => {
    latestInput.current = value;
    fetchAddresses(value);
  };

  const handleOnChange = (value: string) => {
    if (value.length > 0) {
      handleGetAddresses(value);
    } else {
      latestInput.current = '';
      setOptions((current) => (current.length === 0 ? current : []));
    }

    onChange && onChange(value);
  };

  useEffect(() => {
    if (defaultValue.length > 0) {
      handleGetAddresses(defaultValue);
    }
  }, [defaultValue]);

  return (
    <Flex width="100%" flexDirection="column">
      <TextFieldComponent
        aria-label={ariaLabel}
        aria-describedby={errorMessage ? 'address-error' : undefined}
        autocompleteFooter={<GoogleFooter />}
        autocompleteOptions={options}
        autoFocus={autoFocus}
        clearIcon={clearIcon}
        defaultValue={defaultValue}
        disableMobileInputStyling={disableMobileInputStyling}
        label={label}
        labelHidden={labelHidden}
        name={name}
        onBlur={onBlur}
        onChange={handleOnChange}
        onSelection={onSelection}
        placeholder={placeholder}
        required
        startIcon={
          locationIcon && <LocationPinFilledIcon size="sm" color="grays-mid" />
        }
        data-testid={testId}
        width="100%"
      />
      {errorMessage && (
        <Typography mt={16} color="error" id="address-error">
          {errorMessage}
        </Typography>
      )}
    </Flex>
  );
};
